import { useState, useEffect, useCallback } from "react";
import { getAutopilot, updateAutopilot, getAutopilotLogs, runAutopilotNow } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { log } from "../../../lib/log";

const RUN_TITLES = { send: "Envoi effectué ✓", running: "Agent en cours d'exécution…" };
const runDescription = (r) => (r.action === "send" ? `${r.societe} (${r.kind})` : r.reason || "");

export const useAutopilot = () => {
  const [s, setS] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sentToday, setSentToday] = useState(0);
  const [status, setStatus] = useState({ jobs: {}, cron: false });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [set, lg] = await Promise.all([getAutopilot(), getAutopilotLogs()]);
      setS(set.data);
      setLogs(lg.data.logs);
      setSentToday(lg.data.sent_today);
      setStatus({ jobs: lg.data.jobs || {}, cron: !!lg.data.cron_configured });
    } catch (err) {
      log.error("Chargement du pilote automatique :", err);
      setError(err?.response?.data?.detail || "Impossible de charger le pilote automatique");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const res = await updateAutopilot({ ...s, ...extra });
      setS(res.data);
      toast({ title: "Paramètres enregistrés ✓" });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec de l'enregistrement", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !s.enabled;
    patch("enabled", next);
    await save({ enabled: next });
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = (await runAutopilotNow()).data;
      toast({ title: RUN_TITLES[r.action] || `Action : ${r.action}`, description: runDescription(r) });
      load();
      if (r.action === "running") setTimeout(load, 45000);
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec de l'exécution", variant: "destructive" });
    } finally {
      setRunning(false);
    }
  };

  return { s, logs, sentToday, status, error, saving, running, load, patch, save, toggleEnabled, runNow };
};
